import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, CreateSliderData, UpdateSliderData } from '../services/api';
import toast from 'react-hot-toast';

export const useSliders = () => {
  return useQuery({
    queryKey: ['sliders'],
    queryFn: apiService.getSliders,
  });
};

export const useSlider = (id: string) => {
  return useQuery({
    queryKey: ['slider', id],
    queryFn: () => apiService.getSlider(id),
    enabled: !!id,
  });
};

export const useCreateSlider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSliderData) => apiService.createSlider(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sliders'] });
      toast.success('Slider created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create slider');
    },
  });
};

export const useUpdateSlider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSliderData }) =>
      apiService.updateSlider(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sliders'] });
      toast.success('Slider updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update slider');
    },
  });
};

export const useToggleSlider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiService.toggleSlider(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sliders'] });
      toast.success('Slider status updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to toggle slider');
    },
  });
};

export const useDeleteSlider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiService.deleteSlider(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sliders'] });
      toast.success('Slider deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete slider');
    },
  });
};

export const useUploadSliderImage = () => {
  return useMutation({
    mutationFn: ({
      file,
      language,
      cropWidth,
      cropHeight,
    }: {
      file: File;
      language: string;
      cropWidth?: number;
      cropHeight?: number;
    }) => apiService.uploadSliderImage(file, language, cropWidth, cropHeight),
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload image');
    },
  });
};
